/**
 * RFC 5545 (iCalendar RRULE) utilities for recurrence.
 * Maps UI state to/from rrule strings for keepContact.
 */
import { Frequency, RRule, rrulestr } from 'rrule'
import type { Options } from 'rrule'

/** Simple recurrence types */
export type SimpleRecurrenceType = 'never' | 'day' | 'week' | '2weeks' | 'month' | '6weeks' | '3months' | '6months' | 'year'
export const RECURRENCE_TYPE_CUSTOM = 'custom' as const

/** UI state for custom recurrence */
export interface CustomRecurrenceState {
  freq: 'day' | 'week' | 'month' | 'year'
  interval: number
  /** Week: day indices 0=Sun..6=Sat */
  byDay: number[]
  /** Month: 1-31, or 32 for last day of month */
  byMonthDay: number[]
  /** Year: month indices 1-12 */
  byMonth: number[]
  endType: 'never' | 'until' | 'count'
  until: Date | null
  count: number
}

/** Our weekday ids: 0=Sun, 1=Mon, ... 6=Sat. rrule uses 0=MO..6=SU, so map ours to rrule numbers. */
const WEEKDAY_IDS_TO_RRULE = [6, 0, 1, 2, 3, 4, 5] as const

const FREQ_MAP = {
  day: Frequency.DAILY,
  week: Frequency.WEEKLY,
  month: Frequency.MONTHLY,
  year: Frequency.YEARLY,
} as const

export function buildRrule(options: {
  type: SimpleRecurrenceType | typeof RECURRENCE_TYPE_CUSTOM
  dtstart: Date
  custom?: CustomRecurrenceState
}): string | null {
  const { type, dtstart, custom } = options

  if (type === 'never') return null

  const opts: Partial<Options> = {
    dtstart: new Date(dtstart),
    freq: Frequency.DAILY,
    interval: 1,
  }

  if (type === RECURRENCE_TYPE_CUSTOM && custom) {
    opts.freq = FREQ_MAP[custom.freq]
    opts.interval = custom.interval
    if (custom.freq === 'week' && custom.byDay.length > 0) {
      opts.byweekday = custom.byDay.map((d) => WEEKDAY_IDS_TO_RRULE[d])
    }
    if (custom.freq === 'month' && custom.byMonthDay.length > 0) {
      opts.bymonthday = custom.byMonthDay.map((d) => (d === 32 ? -1 : d))
    }
    if (custom.freq === 'year' && custom.byMonth.length > 0) {
      opts.bymonth = custom.byMonth
    }
    if (custom.freq === 'year' && custom.byMonthDay.length > 0) {
      opts.bymonthday = custom.byMonthDay.map((d) => (d === 32 ? -1 : d))
    }
    if (custom.endType === 'until' && custom.until) {
      opts.until = new Date(custom.until)
    }
    if (custom.endType === 'count' && custom.count > 0) {
      opts.count = custom.count
    }
  } else {
    switch (type) {
      case 'day':
        opts.freq = Frequency.DAILY
        opts.interval = 1
        opts.byweekday = undefined
        opts.bymonthday = undefined
        opts.bymonth = undefined
        break
      case 'week':
        opts.freq = Frequency.WEEKLY
        opts.interval = 1
        opts.byweekday = [WEEKDAY_IDS_TO_RRULE[dtstart.getDay()]]
        opts.bymonthday = undefined
        opts.bymonth = undefined
        break
      case 'month':
        opts.freq = Frequency.MONTHLY
        opts.interval = 1
        opts.byweekday = undefined
        opts.bymonthday = [dtstart.getDate()]
        opts.bymonth = undefined
        break
      case '2weeks':
        opts.freq = Frequency.WEEKLY
        opts.interval = 2
        opts.byweekday = [WEEKDAY_IDS_TO_RRULE[dtstart.getDay()]]
        opts.bymonthday = undefined
        opts.bymonth = undefined
        break
      case '6weeks':
        opts.freq = Frequency.WEEKLY
        opts.interval = 6
        opts.byweekday = [WEEKDAY_IDS_TO_RRULE[dtstart.getDay()]]
        opts.bymonthday = undefined
        opts.bymonth = undefined
        break
      case '3months':
        opts.freq = Frequency.MONTHLY
        opts.interval = 3
        opts.byweekday = undefined
        opts.bymonthday = [dtstart.getDate()]
        opts.bymonth = undefined
        break
      case '6months':
        opts.freq = Frequency.MONTHLY
        opts.interval = 6
        opts.byweekday = undefined
        opts.bymonthday = [dtstart.getDate()]
        opts.bymonth = undefined
        break
      case 'year':
        opts.freq = Frequency.YEARLY
        opts.interval = 1
        opts.byweekday = undefined
        opts.bymonthday = undefined
        opts.bymonth = [dtstart.getMonth() + 1]
        break
      default:
        return null
    }
  }

  const rule = new RRule(opts)
  return rule.toString()
}

export function parseRrule(
  rrule: string | null | undefined,
  dtstart: Date
): {
  type: SimpleRecurrenceType | typeof RECURRENCE_TYPE_CUSTOM
  custom: CustomRecurrenceState | null
} {
  if (!rrule || rrule.trim() === '') {
    return { type: 'never', custom: null }
  }

  try {
    const parsed = rrulestr(rrule, { dtstart: new Date(dtstart) })
    if (!(parsed instanceof RRule)) {
      return { type: 'never', custom: null }
    }
    const options = parsed.options

    const freq = options.freq
    const interval = options.interval ?? 1
    const byweekday = options.byweekday
    const bymonthday = options.bymonthday
    const bymonth = options.bymonth
    const until = options.until
    const count = options.count

    const dtDay = dtstart.getDate()
    const dtMonth = dtstart.getMonth() + 1
    const bymonthdayArr = bymonthday ? (Array.isArray(bymonthday) ? bymonthday : [bymonthday]) : []
    const bymonthArr = bymonth ? (Array.isArray(bymonth) ? bymonth : [bymonth]) : []
    const byweekdayArr = byweekday ? (Array.isArray(byweekday) ? byweekday : [byweekday]) : []
    const byweekdayLen = byweekdayArr.length

    const isDefaultMonthly =
      freq === Frequency.MONTHLY &&
      (bymonthdayArr.length === 0 || (bymonthdayArr.length === 1 && bymonthdayArr[0] === dtDay))
    const isDefaultYearly =
      freq === Frequency.YEARLY &&
      (bymonthArr.length === 0 || (bymonthArr.length === 1 && bymonthArr[0] === dtMonth))

    const isSimple =
      !until &&
      !count &&
      ((interval === 1 &&
        (freq === Frequency.DAILY ||
          (freq === Frequency.WEEKLY && byweekdayLen <= 1) ||
          (freq === Frequency.MONTHLY && isDefaultMonthly && byweekdayLen === 0) ||
          (freq === Frequency.YEARLY && isDefaultYearly && byweekdayLen === 0))) ||
      (freq === Frequency.WEEKLY && interval === 2 && byweekdayLen <= 1) ||
      (freq === Frequency.WEEKLY && interval === 6 && byweekdayLen <= 1) ||
      (freq === Frequency.MONTHLY && interval === 3 && isDefaultMonthly && byweekdayLen === 0) ||
      (freq === Frequency.MONTHLY && interval === 6 && isDefaultMonthly && byweekdayLen === 0))

    if (isSimple) {
      if (freq === Frequency.DAILY) return { type: 'day', custom: null }
      if (freq === Frequency.WEEKLY && interval === 1) return { type: 'week', custom: null }
      if (freq === Frequency.WEEKLY && interval === 2) return { type: '2weeks', custom: null }
      if (freq === Frequency.WEEKLY && interval === 6) return { type: '6weeks', custom: null }
      if (freq === Frequency.MONTHLY && interval === 1) return { type: 'month', custom: null }
      if (freq === Frequency.MONTHLY && interval === 3) return { type: '3months', custom: null }
      if (freq === Frequency.MONTHLY && interval === 6) return { type: '6months', custom: null }
      if (freq === Frequency.YEARLY) return { type: 'year', custom: null }
    }

    const freqKeyMap: Record<number, 'day' | 'week' | 'month' | 'year'> = {
      [Frequency.DAILY]: 'day',
      [Frequency.WEEKLY]: 'week',
      [Frequency.MONTHLY]: 'month',
      [Frequency.YEARLY]: 'year',
    }
    const freqKey = freqKeyMap[freq] ?? 'day'
    /** rrule: 0=MO, 1=TU, ..., 6=SU. Ours: 0=Sun, 1=Mon, ..., 6=Sat */
    const rruleWeekdayToOurs = (n: number) => (n + 1) % 7
    const byDay: number[] = byweekday
      ? (Array.isArray(byweekday) ? byweekday : [byweekday]).map((wd) => {
          const n = typeof wd === 'number' ? wd : (wd as { weekday: number }).weekday
          return rruleWeekdayToOurs(n)
        })
      : []
    const byMonthDay: number[] = bymonthday
      ? (Array.isArray(bymonthday) ? bymonthday : [bymonthday]).map((d) => (d === -1 ? 32 : d))
      : []
    const byMonth: number[] = bymonth ? (Array.isArray(bymonth) ? bymonth : [bymonth]) : []

    const custom: CustomRecurrenceState = {
      freq: freqKey,
      interval,
      byDay,
      byMonthDay,
      byMonth,
      endType: count ? 'count' : until ? 'until' : 'never',
      until: until ? new Date(until) : null,
      count: count ?? 0,
    }

    return { type: RECURRENCE_TYPE_CUSTOM, custom }
  } catch {
    return { type: 'never', custom: null }
  }
}

export type TranslateFn = (key: string, params?: Record<string, any>) => string

export function getRecurrenceLabels(t: TranslateFn): Record<string, string> {
  return {
    never: t('recurrence.never'),
    day: t('recurrence.everyDay'),
    week: t('recurrence.everyWeek'),
    '2weeks': t('recurrence.every2Weeks'),
    '6weeks': t('recurrence.every6Weeks'),
    month: t('recurrence.everyMonth'),
    '3months': t('recurrence.every3Months'),
    '6months': t('recurrence.every6Months'),
    year: t('recurrence.everyYear'),
    custom: t('recurrence.custom'),
  }
}

/** Format rrule for display */
export function formatRecurrenceLabel(rrule: string | null | undefined, keepDate: string | Date, t: TranslateFn): string {
  const labels = getRecurrenceLabels(t)
  if (!rrule) return labels.never
  const { type } = parseRrule(rrule, new Date(keepDate))
  return labels[type] ?? labels.never
}

export function labelFromRrule(rrule: string | null | undefined, dtstart: Date, t: TranslateFn): string {
  return formatRecurrenceLabel(rrule, dtstart, t)
}

// rrule counts in UTC. A series is repeated on the user's clock instead: its
// start is handed over as if the local date and hour were UTC ones, and the
// answers are read back the same way. Otherwise a rendez-vous at midnight (an
// all-day one) slips to the day before or after once the clock has changed,
// and « every Monday » is taken for the UTC Monday.
const asUtcClock = (date: Date) =>
  new Date(
    Date.UTC(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      date.getHours(),
      date.getMinutes(),
      date.getSeconds(),
      date.getMilliseconds(),
    ),
  )
const asLocalClock = (date: Date) =>
  new Date(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
    date.getUTCHours(),
    date.getUTCMinutes(),
    date.getUTCSeconds(),
    date.getUTCMilliseconds(),
  )

// The rule read on the user's clock. Its end (UNTIL) is a real instant: it is
// moved onto that clock like the start, otherwise the last occurrence of a
// series is compared an hour or two off and goes missing (east of Greenwich)
// or one too many shows (west of it).
const readOnLocalClock = (rrule: string, dtstart: Date) => {
  const rule = rrulestr(rrule, { dtstart: asUtcClock(new Date(dtstart)) })
  const { until } = rule.origOptions
  return until ? new RRule({ ...rule.origOptions, until: asUtcClock(until) }) : rule
}

/**
 * The first occurrence on or after `from`, or null when the series has ended
 * (or the rule cannot be read). Occurrences keep the local day and hour of
 * `dtstart`.
 */
export function nextOccurrenceOnOrAfter(
  rrule: string,
  dtstart: Date,
  from: Date,
): Date | null {
  try {
    const next = readOnLocalClock(rrule, dtstart).after(asUtcClock(from), true)
    return next ? asLocalClock(next) : null
  } catch {
    return null
  }
}

/**
 * The occurrences from `from` to `to`, both included, `max` at most. They
 * keep the local day and hour of `dtstart`.
 */
export function occurrencesBetween(
  rrule: string,
  dtstart: Date,
  from: Date,
  to: Date,
  max = 400,
): Date[] {
  try {
    return readOnLocalClock(rrule, dtstart)
      .between(asUtcClock(from), asUtcClock(to), true, (_, i) => i < max)
      .map(asLocalClock)
  } catch {
    return []
  }
}

/** Default custom recurrence state */
export const defaultCustomRecurrence: CustomRecurrenceState = {
  freq: 'day',
  interval: 1,
  byDay: [],
  byMonthDay: [],
  byMonth: [],
  endType: 'never',
  until: null,
  count: 10,
}
